/*  MUI  */
import { OpenInNew } from '@mui/icons-material';
import {
    Card,
    CardContent,
    Link as ExternalLink,
    TableCell,
    Typography
} from '@mui/material';

/*  Shared  */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { DateFormat } from 'application/Shared/Components/Core';
import {
    StyledIncidentImpact,
    StyledIncidentStatus
} from 'application/Shared/Components/Incidents';
import { ManagedTable } from 'application/Shared/Components/Table';
import { INCIDENT_STATUS } from 'application/Shared/Constants';

/*  Objects  */
import { Application } from 'objects/Application';

/*  React  */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const headers = (t) => [
    {
        align: 'left',
        field: 'name',
        renderHeader: <strong>{t('Common.application')}</strong>,
        sortable: true,
        sortableFieldType: 'string',
        sortableKey: 'name',
        width: '20%'
    },
    {
        align: 'left',
        field: 'incidentImpact',
        key: 'incidentImpact',
        renderHeader: <strong>{t('Common.impact')}</strong>,
        sortable: true,
        sortableFieldType: 'string',
        sortableKey: 'incidentImpact',
        width: '10%'
    },
    {
        align: 'left',
        field: 'incidentStatus',
        renderHeader: <strong>{t('Common.status')}</strong>,
        sortable: true,
        sortableFieldType: 'string',
        sortableKey: 'incidentStatus',
        width: '10%'
    },
    {
        align: 'left',
        field: 'lastIncidentUpdateDescription',
        renderHeader: <strong>{t('Common.description')}</strong>,
        width: '40%'
    },
    {
        align: 'left',
        field: 'lastIncidentUpdate',
        renderHeader: <strong>{t('Common.lastIncidentUpdate')}</strong>,
        sortable: true,
        sortableFieldType: 'date',
        sortableKey: 'lastIncidentUpdate',
        width: '15%'
    },
    {
        align: 'right',
        field: 'link',
        renderHeader: <strong>{t('Common.appStatusPage')}</strong>,
        width: '5%'
    }
];

const ExternalAppsStatusRow = ({ row }) => {
    const {
        application,
        incidentImpact,
        incidentStatus,
        lastIncidentUpdate,
        lastIncidentUpdateDescription,
        incidentExternalLink
    } = row;

    const isResolved =
        incidentStatus.toLowerCase() === INCIDENT_STATUS.RESOLVED;

    return (
        <Application nameHasLink data={application}>
            <TableCell>
                <ApplicationNameLinkCell />
            </TableCell>
            <TableCell>
                <StyledIncidentImpact
                    incidentImpact={incidentImpact?.toLowerCase()}
                    isResolved={isResolved}
                />
            </TableCell>
            <TableCell>
                <StyledIncidentStatus
                    incidentStatus={incidentStatus?.toLowerCase()}
                    isResolved={isResolved}
                />
            </TableCell>
            <TableCell>{lastIncidentUpdateDescription}</TableCell>
            <TableCell>
                <DateFormat date={lastIncidentUpdate} noTz />
            </TableCell>
            <TableCell align="right">
                <ExternalLink
                    href={incidentExternalLink}
                    rel="noopener noreferrer nofollow"
                    target="_blank"
                    disabled={!!incidentExternalLink}
                >
                    <OpenInNew
                        sx={{
                            color: 'button.primary'
                        }}
                    />
                </ExternalLink>
            </TableCell>
        </Application>
    );
};

const tableConstants = Object.freeze({
    renderBodyFn: (row) => <ExternalAppsStatusRow row={row} />,
    searchFields: [
        'name',
        'incidentImpact',
        'incidentStatus',
        'lastIncidentUpdateDescription'
    ]
});

export const ExternalAppsStatus = () => {
    const { t } = useTranslation();

    const memoized = useMemo(
        () => ({
            headers: headers(t)
        }),
        [t]
    );

    return (
        <Card variant="outlined">
            <CardContent>
                <Typography variant="h5">
                    {t('Applications.recentSaaSServiceInterruptions')}
                </Typography>
                <hr />
                <ManagedTable
                    headers={memoized.headers}
                    renderBody={tableConstants.renderBodyFn}
                    renderBodyKey="id"
                    renderFooter
                    rowsApi="/appStatus"
                    rowsApiPreload
                    rowsPerPage={5}
                    searchEnabled
                    sortDirection="ASC"
                    sortFieldType="string"
                    sortKey="incidentImpact"
                    searchFields={tableConstants.searchFields}
                />
            </CardContent>
        </Card>
    );
};
